import React, { useEffect, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { PeekPaper, PeekStack, PeekTypography, useTheme } from "@piiqtechnologies/ui-components";
import { AiDiscoverabilityResultNavigation } from "./AiDiscoverabilityResultNavigation";
import messages from "./messages.i18n";
export const AiDiscoverabilityResultContent = ({ results }) => {
    var _a;
    const intl = useIntl();
    const theme = useTheme();
    const [activeSection, setActiveSection] = useState("");
    const contentRef = useRef(null);
    const sectionRefs = useRef({});
    const sections = useMemo(() => {
        var _a;
        return ((_a = results === null || results === void 0 ? void 0 : results.result.results.map((result, index) => ({
            id: `${result.originLLM || "result"}-${index}`,
            label: result.originLLM || intl.formatMessage(messages.aiDiscoverabilityResultLabel, { index: index + 1 }),
        }))) !== null && _a !== void 0 ? _a : []);
    }, [intl, results === null || results === void 0 ? void 0 : results.result.results]);
    useEffect(() => {
        if (!sections.length) {
            setActiveSection("");
            return;
        }
        setActiveSection((current) => {
            const sectionExists = sections.some((section) => section.id === current);
            return sectionExists ? current : sections[0].id;
        });
    }, [sections]);
    useEffect(() => {
        const container = contentRef.current;
        if (!container || !sections.length)
            return undefined;
        const handleScroll = () => {
            const scrollTop = container.scrollTop;
            const threshold = 80;
            for (let i = sections.length - 1; i >= 0; i -= 1) {
                const section = sections[i];
                const element = sectionRefs.current[section.id];
                if (element) {
                    const elementTop = element.offsetTop;
                    if (scrollTop >= elementTop - threshold) {
                        setActiveSection(section.id);
                        return;
                    }
                }
            }
        };
        container.addEventListener("scroll", handleScroll);
        handleScroll();
        return () => container.removeEventListener("scroll", handleScroll);
    }, [sections]);
    const handleNavigationClick = (sectionId) => {
        const element = sectionRefs.current[sectionId];
        if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    };
    const hasResults = Boolean((_a = results === null || results === void 0 ? void 0 : results.result.results) === null || _a === void 0 ? void 0 : _a.length);
    return (React.createElement(PeekPaper, { variant: "outlined", sx: {
            py: theme.spacing(2),
            px: theme.spacing(2.5),
            textAlign: "left",
            border: `1px dashed ${theme.palette.grey[200]}`,
        } }, hasResults ? (React.createElement(PeekStack, { direction: "row", alignItems: "flex-start", spacing: 3 },
        React.createElement(AiDiscoverabilityResultNavigation, { sections: sections, activeSection: activeSection, onSectionClick: handleNavigationClick }),
        React.createElement(PeekStack, { ref: contentRef, spacing: 2, sx: { flex: 1, maxHeight: theme.spacing(90), overflowY: "auto", pr: 1 } }, results === null || results === void 0 ? void 0 : results.result.results.map((result, index) => {
            var _a, _b;
            const sectionId = (_b = (_a = sections[index]) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : `result-${index}`;
            return (React.createElement(PeekStack, { key: sectionId, ref: (element) => {
                    if (sectionId) {
                        // @ts-ignore
                        sectionRefs.current[sectionId] = element;
                    }
                }, spacing: 0.5 },
                React.createElement(PeekTypography, { fontWeight: theme.typography.fontWeightMedium }, result.originLLM ||
                    intl.formatMessage(messages.aiDiscoverabilityResultLabel, { index: index + 1 })),
                React.createElement(PeekTypography, null, result.result)));
        })))) : (React.createElement(PeekTypography, null, intl.formatMessage(messages.aiDiscoverabilityResultsPlaceholder)))));
};
export default AiDiscoverabilityResultContent;
